// Renders today's actual moon phase as an SVG + a scatter of twinkling stars.
// The moon uses a single closed SVG path for the dark side — clean crescent
// geometry, no double-terminator artefacts.

const REF_NEW_MOON = Date.UTC(2000, 0, 6, 18, 14) / 86400000;
const SYNODIC = 29.5305882;

// 0 = new moon, 0.5 = full moon, 1 = new again
function moonPhaseFraction(date = new Date()) {
  const days = date.getTime() / 86400000 - REF_NEW_MOON;
  return ((days / SYNODIC) % 1 + 1) % 1;
}

function moonLabel(f) {
  if (f < 0.03 || f > 0.97) return "New moon";
  if (f < 0.22) return "Waxing crescent";
  if (f < 0.28) return "First quarter";
  if (f < 0.47) return "Waxing gibbous";
  if (f < 0.53) return "Full moon";
  if (f < 0.72) return "Waning gibbous";
  if (f < 0.78) return "Last quarter";
  return "Waning crescent";
}

// Builds the SVG path for the DARK side of the moon disc at phase f.
// The dark region is bounded by (1) one semicircle of the moon and
// (2) an elliptical terminator arc. Both arc directions flip across the
// full-moon and first-quarter/last-quarter marks.
function darkSidePath(f, cx, cy, r) {
  // At/near full moon we don't paint anything.
  if (f > 0.485 && f < 0.515) return "";
  // At/near new moon we paint the whole disc.
  if (f < 0.015 || f > 0.985) {
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`;
  }
  const termRx = r * Math.abs(Math.cos(2 * Math.PI * f));
  let sweepA, sweepB;
  if (f < 0.5) {
    // Waxing: dark hugs LEFT semicircle of disc.
    // sweepA=0 → top→bottom arc curves through the LEFT half.
    // sweepB: bottom→top on the terminator ellipse — sweep=0 curves through
    // the RIGHT (needed for crescent, so the enclosed dark is BIG),
    // sweep=1 curves through the LEFT (needed for gibbous, so the enclosed
    // dark is only a thin left sliver).
    sweepA = 0;
    sweepB = f < 0.25 ? 0 : 1;
  } else {
    // Waning: mirror image — dark hugs RIGHT semicircle.
    sweepA = 1;
    sweepB = f > 0.75 ? 1 : 0;
  }
  return `M ${cx} ${cy - r} A ${r} ${r} 0 0 ${sweepA} ${cx} ${cy + r} A ${termRx} ${r} 0 0 ${sweepB} ${cx} ${cy - r} Z`;
}

// Stable star field (seeded PRNG so positions don't shuffle across renders).
function stableStars(count = 60) {
  const stars = [];
  let seed = 1729;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let i = 0; i < count; i++) {
    stars.push({
      left: rand() * 100,
      top: rand() * 70,
      size: 1 + rand() * 2.2,
      delay: rand() * 6,
      dur: 3 + rand() * 5,
    });
  }
  return stars;
}
const STARS = stableStars(60);

export default function NightSky() {
  const f = moonPhaseFraction();
  const cx = 100;
  const cy = 100;
  const r = 90;
  const darkPath = darkSidePath(f, cx, cy, r);

  return (
    <>
      {STARS.map((s, i) => (
        <span
          key={i}
          className="twinkle-star"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            "--dur": `${s.dur}s`,
            "--delay": `${s.delay}s`,
          }}
        />
      ))}
      <svg
        className="moon-svg"
        viewBox="0 0 200 200"
        aria-label={moonLabel(f)}
      >
        <defs>
          <radialGradient id="moon-glow" cx="35%" cy="35%" r="72%">
            <stop offset="0%" stopColor="#fff7d6" />
            <stop offset="55%" stopColor="#ecd9a0" />
            <stop offset="100%" stopColor="#a88a4d" />
          </radialGradient>
        </defs>

        {/* Full illuminated disc. No gold ring — when the moon is near-full it
            should read as a warm cream disc; near-new we still keep the disc
            silhouette by tinting the "dark" fill to a very dim charcoal (see
            below) instead of ringing it in gold. */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="url(#moon-glow)"
        />

        {/* Subtle craters */}
        <circle cx={cx - 25} cy={cy - 20} r="7" fill="#b89355" opacity="0.35" />
        <circle cx={cx + 18} cy={cy + 6} r="5" fill="#b89355" opacity="0.30" />
        <circle cx={cx - 6} cy={cy + 30} r="9" fill="#b89355" opacity="0.25" />
        <circle cx={cx + 30} cy={cy - 25} r="4" fill="#b89355" opacity="0.30" />
        <circle cx={cx - 40} cy={cy + 15} r="3" fill="#b89355" opacity="0.25" />

        {/* Dark side — soft charcoal so the disc silhouette is still hintable
            during a new moon (via slight contrast against the background) but
            never rings in gold. */}
        {darkPath && (
          <path d={darkPath} fill="#1a1613" opacity="0.92" />
        )}

        <title>{moonLabel(f)} — {(f * 100).toFixed(0)}% of lunar cycle</title>
      </svg>
    </>
  );
}
